/**
 * The export template editor — one component for the functional
 * administrator's organization templates (TemplateManager) and the
 * designer's standard templates (the transaction designer's Export action).
 *
 * Name, file name, who may use it, default, "let people choose forms",
 * default format; the header's columns and each included line form's
 * columns (ColumnTable — drag and drop); the layout (lines on a separate
 * sheet / on the same sheet), header values on line rows (repeat all /
 * repeat key columns only / show once), records without lines, and the
 * sheet options. The preview below is built by the engine (`onPreview`), so
 * it is exactly what the file will hold.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import ChipInput from '../ChipInput'
import { Field, Note, Seg, Switch, input, meta } from '../../messaging/ui'
import { tw } from '../../testHooks'
import { ColumnTable } from './ColumnTable'
import { SheetPreview } from './SheetPreview'
import type {
  ExportCatalogForm, ExportFormat, ExportPreview, ExportTemplate, HeaderOnLines, LayoutMode,
} from './types'
import { EXPORT_CONFIG, exportErrorText } from './types'

export function TemplateEditor({ template, catalog, formats, onChange, readOnly, onPreview, roleHint }: {
  template: ExportTemplate
  catalog: ExportCatalogForm[]
  formats: ExportFormat[]
  onChange: (t: ExportTemplate) => void
  readOnly?: boolean
  /** Builds the engine preview for a draft; absent ⇒ no preview. */
  onPreview?: (draft: ExportTemplate) => Promise<ExportPreview>
  roleHint?: string
}) {
  const t = template
  const header = catalog[0]
  const lineForms = catalog.slice(1)
  const set = (p: Partial<ExportTemplate>) => onChange({ ...t, ...p })
  const setLayout = (p: Partial<ExportTemplate['layout']>) => onChange({ ...t, layout: { ...t.layout, ...p } })
  const setSheet = (p: Partial<ExportTemplate['sheet']>) => onChange({ ...t, sheet: { ...t.sheet, ...p } })
  const same = t.layout.mode === 'same_sheet'
  const included = new Set(t.details.map(d => d.form_no))

  const toggleForm = (f: ExportCatalogForm, on: boolean) => {
    if (on) {
      set({ details: [...t.details, { form_no: f.form_no, sheet: f.label.slice(0, 31),
        columns: f.columns.filter(c => !c.hidden && !c.pk).slice(0, 4)
          .map(c => ({ column: c.key, heading: c.label, value: 'display' as const })) }] })
    } else {
      set({ details: t.details.filter(d => d.form_no !== f.form_no) })
    }
  }
  const setMode = (mode: LayoutMode) => {
    if (mode === 'same_sheet' && t.details.length > 1) {
      onChange({ ...t, layout: { ...t.layout, mode }, details: t.details.slice(0, 1) })
    } else setLayout({ mode })
  }

  // ── engine preview, debounced ──
  const [preview, setPreview] = useState<ExportPreview | null>(null)
  const [pvError, setPvError] = useState<string | null>(null)
  const [pvBusy, setPvBusy] = useState(false)
  const seq = useRef(0)
  const key = useMemo(() => JSON.stringify(t), [t])
  useEffect(() => {
    if (!onPreview) return
    const my = ++seq.current
    setPvBusy(true)
    const h = window.setTimeout(() => {
      onPreview(t).then((p) => {
        if (my !== seq.current) return
        setPreview(p); setPvError(null)
      }).catch((err: unknown) => {
        if (my !== seq.current) return
        const d = exportErrorText(err)
        setPvError(`${d.message}${d.problems.length ? ` ${d.problems.join(' ')}` : ''}`)
      }).finally(() => { if (my === seq.current) setPvBusy(false) })
    }, 450)
    return () => window.clearTimeout(h)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, onPreview])

  if (!header) return <Note tone="warning">This screen has no forms to export.</Note>

  return (
    <div style={{ display: 'grid', gap: 18, gridTemplateColumns: 'minmax(0, 1fr)' }} {...tw('group', 'export-template-editor')}>
      <Section>
        <Row>
          <Field title="Name" htmlFor="xt-name" style={{ flex: '1 1 240px' }}>
            <input id="xt-name" style={input} value={t.name} disabled={readOnly}
              maxLength={EXPORT_CONFIG.limits.maxNameLength}
              onChange={e => set({ name: e.target.value })} {...tw('field', 'export-template:name')} />
          </Field>
          <Field title="File name" htmlFor="xt-file" style={{ flex: '1 1 240px' }}>
            <input id="xt-file" style={{ ...input, fontFamily: "'IBM Plex Mono', monospace" }} value={t.file_name}
              disabled={readOnly} maxLength={EXPORT_CONFIG.limits.maxFileNameLength}
              onChange={e => set({ file_name: e.target.value })} />
            <span style={meta}>{'{date}'} becomes the export date, {'{screen}'} the screen's name.</span>
          </Field>
        </Row>
        <Row>
          <Field title="Who can use it" style={{ flex: '1 1 300px' }}>
            <ChipInput values={t.roles} disabled={readOnly} onChange={(roles) => set({ roles })}
              placeholder="Everyone who can export — or type a role and press Enter" />
            <span style={meta}>{roleHint || 'A role narrows who sees the template. It never gives Export to anyone.'}</span>
          </Field>
          <div style={{ flex: '1 1 260px', display: 'grid', gap: 10, alignContent: 'start' }}>
            <SwitchRow title="Default template" hint="Selected first when people open Export."
              on={t.is_default} disabled={readOnly} onChange={(v) => set({ is_default: v })} />
            <SwitchRow title="Let people choose forms"
              hint="When exporting, people may leave out the header or a line form."
              on={t.form_choice} disabled={readOnly} onChange={(v) => set({ form_choice: v })} />
            <Field title="Default format">
              <Seg ariaLabel="Default format" value={t.format} disabled={readOnly}
                onChange={(v) => set({ format: v as ExportFormat })}
                options={formats.map(f => ({ value: f, label: EXPORT_CONFIG.formats.find(x => x.id === f)?.label || f }))} />
            </Field>
          </div>
        </Row>
      </Section>

      <Section title={`Header: ${header.label}`}
        hint="Tick Key on the columns that identify a record. They start every line row on a separate sheet, and repeat on line rows when you choose “Repeat key columns only”.">
        <ColumnTable form={header} cols={t.header} isHeader readOnly={readOnly}
          onChange={(header) => set({ header })} />
      </Section>

      {lineForms.length > 0 && (
        <Section title="Lines to include">
          <div style={{ display: 'grid', gap: 8 }}>
            {lineForms.map(f => {
              const on = included.has(f.form_no)
              const blocked = same && !on && t.details.length >= 1
              return (
                <label key={f.form_no} style={{ display: 'flex', gap: 8, alignItems: 'flex-start',
                  cursor: blocked || readOnly ? 'not-allowed' : 'pointer', opacity: blocked ? 0.55 : 1 }}>
                  <input type="checkbox" checked={on} disabled={readOnly || blocked}
                    onChange={e => toggleForm(f, e.target.checked)} {...tw('field', `export-template:form:${f.form_no}`)}
                    style={{ width: 16, height: 16, marginTop: 3, accentColor: 'var(--color-accent, #0061D0)' }} />
                  <span>{f.label}
                    <span style={{ ...meta, display: 'block' }}>
                      {f.columns.length} columns{blocked ? ' · lines on the same sheet take one line form' : ''}
                    </span>
                  </span>
                </label>
              )
            })}
          </div>
        </Section>
      )}

      {t.details.map((d, di) => {
        const f = lineForms.find(x => x.form_no === d.form_no)
        if (!f) return <Note key={d.form_no} tone="warning">Form {d.form_no} is no longer a line form of this screen.</Note>
        return (
          <Section key={d.form_no} title={`Lines: ${f.label}`} right={!same ? (
            <label style={{ ...meta, display: 'flex', gap: 8, alignItems: 'center' }}>Sheet name
              <input style={{ ...input, width: 180, height: 32 }} value={d.sheet} disabled={readOnly}
                maxLength={EXPORT_CONFIG.limits.maxSheetNameLength}
                onChange={e => set({ details: t.details.map((x, j) => (j === di ? { ...x, sheet: e.target.value } : x)) })} />
            </label>
          ) : undefined}>
            <ColumnTable form={f} cols={d.columns} isHeader={false} readOnly={readOnly}
              onChange={(columns) => set({ details: t.details.map((x, j) => (j === di ? { ...x, columns } : x)) })} />
          </Section>
        )
      })}

      <Section title="Layout of header and lines">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <LayoutCard on={!same} disabled={readOnly} onClick={() => setMode('separate_sheets')}
            title="Lines on a separate sheet"
            hint="One sheet of records and one sheet per line form. Each line row starts with the record's key columns so the sheets link up."
            glyph={[['h', 'h', 'h'], ['h', 'h', 'h'], ['b', 'b', 'b'], ['d', 'd', 'd'], ['d', 'd', 'd']]}
            hook="export-template:layout:separate_sheets" />
          <LayoutCard on={same} disabled={readOnly} onClick={() => setMode('same_sheet')}
            title="Lines on the same sheet"
            hint="One row per line, with the line columns after the header columns."
            glyph={[['h', 'h', 'd', 'd'], ['h', 'h', 'd', 'd'], ['h', 'h', 'd', 'd']]}
            hook="export-template:layout:same_sheet" />
        </div>
        <Row>
          <div style={{ flex: '1 1 280px' }}>
            <span style={labelStyle}>Header values on each line row</span>
            <div style={{ display: 'grid', gap: 8 }}>
              {EXPORT_CONFIG.headerOnLines.map(o => (
                <Radio key={o.id} name="xt-hol" checked={t.layout.header_on_lines === o.id}
                  disabled={readOnly || !same} onChange={() => setLayout({ header_on_lines: o.id as HeaderOnLines })}
                  title={o.label} hint={o.hint} hook={`export-template:header-on-lines:${o.id}`} />
              ))}
            </div>
            {!same && <p style={{ ...meta, margin: '6px 0 0' }}>Applies when lines are on the same sheet.</p>}
          </div>
          <div style={{ flex: '1 1 260px', display: 'grid', gap: 12, alignContent: 'start' }}>
            <div>
              <span style={labelStyle}>Records with no lines</span>
              <div style={{ display: 'grid', gap: 8 }}>
                <Radio name="xt-nol" checked={t.layout.records_without_lines === 'include'} disabled={readOnly || !same}
                  onChange={() => setLayout({ records_without_lines: 'include' })} title="Include them with blank line columns" />
                <Radio name="xt-nol" checked={t.layout.records_without_lines === 'omit'} disabled={readOnly || !same}
                  onChange={() => setLayout({ records_without_lines: 'omit' })} title="Leave them out" />
              </div>
            </div>
            <Check on={t.layout.group_heading_row} disabled={readOnly || !same}
              onChange={(v) => setLayout({ group_heading_row: v })} title="Group heading row"
              hint="The header form's name over its columns, the line form's name over its columns." />
            <Check on={t.sheet.freeze_header} disabled={readOnly} onChange={(v) => setSheet({ freeze_header: v })}
              title="Freeze the heading row" />
            <Check on={t.sheet.filter_buttons} disabled={readOnly} onChange={(v) => setSheet({ filter_buttons: v })}
              title="Filter buttons on the headings" />
            <Check on={t.sheet.about_sheet} disabled={readOnly} onChange={(v) => setSheet({ about_sheet: v })}
              title="Add an “About this export” sheet" hint="Who exported it, when, the template and the rows." />
          </div>
        </Row>
      </Section>

      {onPreview && (
        <Section title="Preview">
          <SheetPreview preview={preview} busy={pvBusy} error={pvError} format={t.format} />
        </Section>
      )}
    </div>
  )
}

const labelStyle: CSSProperties = {
  display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary, #465062)', marginBottom: 6,
}

function Section({ title, hint, right, children }: { title?: string; hint?: string; right?: ReactNode; children: ReactNode }) {
  return (
    <section style={{ display: 'grid', gap: 10, gridTemplateColumns: 'minmax(0, 1fr)', borderTop: title ? '1px solid var(--color-border, #e6eaef)' : 0,
      paddingTop: title ? 14 : 0 }}>
      {(title || right) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          {title && <h3 style={{ margin: 0, fontSize: 14 }}>{title}</h3>}
          {right}
        </div>
      )}
      {hint && <p style={{ ...meta, margin: 0 }}>{hint}</p>}
      {children}
    </section>
  )
}

function Row({ children }: { children: ReactNode }) {
  return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>{children}</div>
}

function SwitchRow({ title, hint, on, onChange, disabled }: {
  title: string; hint: string; on: boolean; onChange: (v: boolean) => void; disabled?: boolean
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 500 }}>{title}</div>
        <div style={meta}>{hint}</div>
      </div>
      <Switch on={on} onChange={onChange} label={title} disabled={disabled} />
    </div>
  )
}

function Radio({ name, checked, onChange, title, hint, disabled, hook }: {
  name: string; checked: boolean; onChange: () => void; title: string; hint?: string; disabled?: boolean; hook?: string
}) {
  return (
    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1 }}>
      <input type="radio" name={name} checked={checked} disabled={disabled} onChange={onChange}
        {...(hook ? tw('field', hook) : {})}
        style={{ width: 16, height: 16, marginTop: 3, accentColor: 'var(--color-accent, #0061D0)' }} />
      <span>{title}{hint && <span style={{ ...meta, display: 'block' }}>{hint}</span>}</span>
    </label>
  )
}

function Check({ on, onChange, title, hint, disabled }: {
  on: boolean; onChange: (v: boolean) => void; title: string; hint?: string; disabled?: boolean
}) {
  return (
    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1 }}>
      <input type="checkbox" checked={on} disabled={disabled} onChange={e => onChange(e.target.checked)}
        style={{ width: 16, height: 16, marginTop: 3, accentColor: 'var(--color-accent, #0061D0)' }} />
      <span>{title}{hint && <span style={{ ...meta, display: 'block' }}>{hint}</span>}</span>
    </label>
  )
}

function LayoutCard({ on, onClick, title, hint, glyph, disabled, hook }: {
  on: boolean; onClick: () => void; title: string; hint: string; glyph: string[][]; disabled?: boolean; hook: string
}) {
  const color = (c: string) => (c === 'd' ? 'var(--color-success, #17703f)' : c === 'b'
    ? 'var(--color-border-strong, #c9d1db)' : 'var(--color-accent, #0061D0)')
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-pressed={on} {...tw('action', hook)}
      style={{ flex: '1 1 260px', display: 'flex', gap: 12, textAlign: 'left', padding: 12, borderRadius: 8,
        cursor: disabled ? 'not-allowed' : 'pointer', font: 'inherit', color: 'var(--color-text, #1b2330)',
        border: `1px solid ${on ? 'var(--color-accent, #0061D0)' : 'var(--color-border-strong, #c9d1db)'}`,
        boxShadow: on ? 'inset 0 0 0 1px var(--color-accent, #0061D0)' : 'none',
        background: on ? 'var(--color-accent-weak, rgba(0,97,208,.10))' : 'var(--color-bg, #fff)' }}>
      <span aria-hidden="true" style={{ display: 'grid', gap: 2, width: 58, flexShrink: 0 }}>
        {glyph.map((row, i) => (
          <span key={i} style={{ display: 'flex', gap: 2 }}>
            {row.map((c, j) => <span key={j} style={{ flex: 1, height: 7, borderRadius: 1, background: color(c),
              opacity: c === 'b' ? 1 : 0.6 }} />)}
          </span>
        ))}
      </span>
      <span><b style={{ display: 'block' }}>{title}</b><span style={meta}>{hint}</span></span>
    </button>
  )
}
